import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from '@mui/material';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { translations } from '../../utils/translations';

function ResultsScreen({ language, onLanguageChange, settlementRange, answers, refCode }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const t = translations[language];

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const formatPhoneNumber = (value) => {
    const phone = value.replace(/\D/g, '');
    if (phone.length < 4) return phone;
    if (phone.length < 7) return `(${phone.slice(0, 3)}) ${phone.slice(3)}`;
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
  };

  const handlePhoneChange = (event) => {
    const formatted = formatPhoneNumber(event.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const isFormValid = () => {
    return (
      formData.name.trim() !== '' &&
      formData.phone.replace(/\D/g, '').length >= 10 &&
      formData.email.includes('@') &&
      consent
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setSubmitting(true);
    setError(null);

    try {
      // Determine the refCode - use "homepage" if no referral code provided
      const effectiveRefCode = refCode || 'homepage';

      // Create the lead document
      const leadData = {
        // Contact info
        name: formData.name.trim(),
        phone: formData.phone,
        email: formData.email.trim().toLowerCase(),
        
        // Answers from estimator
        answers: answers,
        
        // Settlement range
        range: {
          min: settlementRange.min,
          max: settlementRange.max,
          formattedMin: settlementRange.formatted.min,
          formattedMax: settlementRange.formatted.max,
        },
        
        // Referral tracking - "homepage" for direct visitors, actual refCode for referrals
        refCode: effectiveRefCode,
        
        // Consent and compliance
        consentGiven: true,
        consentTimestamp: new Date(),
        
        // Metadata
        language: language,
        createdAt: new Date(),
        status: 'new',
      };

      await addDoc(collection(db, 'leads'), leadData);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting lead:', err);
      setError('There was an error submitting your information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#FDF0D5', py: 4 }}>
        <Container maxWidth="sm">
          {/* Language Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <ToggleButtonGroup
              value={language}
              exclusive
              onChange={onLanguageChange}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  color: '#5C5658',
                  borderColor: '#C6D8D3',
                  '&.Mui-selected': {
                    color: '#EB5E55',
                    bgcolor: 'rgba(235, 94, 85, 0.08)',
                    borderColor: '#EB5E55',
                  },
                },
              }}
            >
              <ToggleButton value="en">{t.languageToggle.en}</ToggleButton>
              <ToggleButton value="es">{t.languageToggle.es}</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Paper
            sx={{
              p: 5,
              bgcolor: '#FFFFFF',
              border: '2px solid #4A9E7F',
              borderRadius: '16px',
              textAlign: 'center',
            }}
          >
            <Box 
              sx={{ 
                width: 72, 
                height: 72, 
                borderRadius: '50%', 
                bgcolor: 'rgba(74, 158, 127, 0.1)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
              }}
            >
              <Typography sx={{ fontSize: '2rem' }}>✓</Typography>
            </Box>
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: '#4A9E7F',
              }}
            >
              {t.success.title}
            </Typography>
            <Typography variant="body1" sx={{ color: '#3A3335', mb: 3, lineHeight: 1.8 }}>
              {t.success.message}
            </Typography>
            <Paper
              sx={{
                p: 3,
                bgcolor: 'rgba(74, 158, 127, 0.08)',
                border: '1px solid rgba(74, 158, 127, 0.3)',
                borderRadius: '12px',
              }}
            >
              <Typography variant="body2" sx={{ color: '#3A6B5A' }}>
                {t.success.note}
              </Typography>
            </Paper>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FDF0D5', py: 4 }}>
      <Container maxWidth="sm">
        {/* Language Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={onLanguageChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: '#5C5658',
                borderColor: '#C6D8D3',
                '&.Mui-selected': {
                  color: '#EB5E55',
                  bgcolor: 'rgba(235, 94, 85, 0.08)',
                  borderColor: '#EB5E55',
                },
              },
            }}
          >
            <ToggleButton value="en">{t.languageToggle.en}</ToggleButton>
            <ToggleButton value="es">{t.languageToggle.es}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Results Card */}
        <Paper
          sx={{
            p: 4,
            bgcolor: '#FFFFFF',
            border: '2px solid #EB5E55',
            borderRadius: '16px',
            mb: 4,
            textAlign: 'center',
          }}
        >
          <Box 
            sx={{ 
              width: 56, 
              height: 56, 
              borderRadius: '50%', 
              bgcolor: 'rgba(235, 94, 85, 0.1)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 1rem',
            }}
          >
            <Typography sx={{ fontSize: '1.5rem' }}>📊</Typography>
          </Box>
          
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 2,
              fontWeight: 700,
              color: '#3A3335',
            }}
          >
            {t.results.title}
          </Typography>
          
          <Typography variant="body1" sx={{ color: '#5C5658', mb: 4 }}>
            {t.results.description}
          </Typography>

          {/* Settlement Range Display */}
          <Box
            sx={{
              py: 4,
              px: 3,
              mb: 4,
              background: 'linear-gradient(135deg, rgba(235, 94, 85, 0.08) 0%, rgba(216, 30, 91, 0.08) 100%)',
              borderRadius: '12px',
              border: '1px solid rgba(235, 94, 85, 0.3)',
            }}
          >
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800,
                color: '#D81E5B',
                fontSize: { xs: '1.75rem', sm: '2.25rem' },
              }}
            >
              {settlementRange.formatted.min} – {settlementRange.formatted.max}
            </Typography>
          </Box>

          <Typography 
            variant="body2" 
            sx={{ 
              color: '#5C5658', 
              fontStyle: 'italic',
              lineHeight: 1.6,
            }}
          >
            {t.results.disclaimer}
          </Typography>
        </Paper>

        {/* Next Step Card */}
        <Paper
          sx={{
            p: 4,
            bgcolor: '#FFFFFF',
            border: '1px solid #C6D8D3',
            borderRadius: '16px',
            mb: 4,
          }}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 2,
              fontWeight: 700,
              color: '#3A3335',
            }}
          >
            {t.results.nextStepTitle}
          </Typography>

          <Typography variant="body1" sx={{ color: '#3A3335', mb: 3, lineHeight: 1.8 }}>
            {t.results.nextStepText}
          </Typography>

          <Typography variant="body1" sx={{ color: '#5C5658', mb: 4, lineHeight: 1.8 }}>
            {t.results.ctaText}
          </Typography>

          {/* Lead Form */}
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label={t.leadForm.nameLabel}
              placeholder={t.leadForm.namePlaceholder}
              value={formData.name}
              onChange={handleInputChange('name')}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            <TextField
              fullWidth
              label={t.leadForm.phoneLabel}
              placeholder={t.leadForm.phonePlaceholder}
              value={formData.phone}
              onChange={handlePhoneChange}
              variant="outlined"
              inputProps={{ maxLength: 14 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            <TextField
              fullWidth
              label={t.leadForm.emailLabel}
              placeholder={t.leadForm.emailPlaceholder}
              value={formData.email}
              onChange={handleInputChange('email')}
              variant="outlined"
              type="email"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            {/* TCPA Consent Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  sx={{
                    color: '#EB5E55',
                    '&.Mui-checked': {
                      color: '#EB5E55',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#5C5658' }}>
                  {t.leadForm.consentText}
                </Typography>
              }
              sx={{ alignItems: 'flex-start', mt: 1 }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 1, borderRadius: '10px' }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={!isFormValid() || submitting}
              sx={{
                py: 2,
                mt: 1,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '10px',
              }}
            >
              {submitting ? t.leadForm.submitting : t.leadForm.submitButton}
            </Button>

            {/* Legal Disclaimer */}
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#7A7577',
                textAlign: 'center',
                mt: 1,
                lineHeight: 1.6,
              }}
            >
              {t.leadForm.legalDisclaimer}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default ResultsScreen;
